import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Grid } from '@material-ui/core';
import PropTypes from 'prop-types';
import { each } from 'lodash';
import {
  setRegion,
  selectDestinations,
  setDays,
  setRange,
  selectPackage,
  selectPlan,
  selectCoverages,
  getPrices,
  selectLimit,
  setAge,
} from '../../redux/modules/travelCalculation';
import TravelCalculator from '../../components/TravelCalculator';
import { loadAvailableDestinations } from '../../redux/modules/travelDestinations';
import CalculatorSummary from '../../components/TravelCalculator/CalculatorSummary';
import EfesContentHeader from '../../components/EfesContentHeader';
import {
  getActiveCoverages,
  getContractSummary,
  getPlansWithAllowedLimitRegionAndAge,
  getBasePrice,
  getOnlyLiteDisallowedCountry,
  getSelectedRegionForSummaryContract,
  getDiscountedPrice,
} from '../../helpers/travel/travelCalcHelpers';
import { bindSummaryContractData } from '../../helpers/travel/bindData';
import { getDiscountPrice } from '../../helpers/price';
import useDeviceResize from '../../hooks/useDeviceResize';
import useDebounce from '../../hooks/useDebounce';
import { EFES_CONTENT_WIDTH, EFES_SUMMARY_WIDTH, FULL_WIDTH } from '../../constants/uiConstants';
import { ACTION_NAME, TRAVEL_CONTRACT_DAYS_DEBOUNCE } from '../../constants';

import TravelCalcHeaderImage from '../../img/travelHeader.png';
import summaryWrapperUseStyles from '../../components/MUI/SummaryWrapper/makeStyles';

const TravelCalculatorContainer = ({
  travelers,
  showAgeInput,
  shouldRequestPrice,
  travelDataSelector,
  formProps,
  actionName,
  hasNarrowView,
  buyButtonHandler,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const summaryWrapperClasses = summaryWrapperUseStyles();
  const [openMobileSummary, setOpenMobileSummary] = useState();
  const isDeviceLgSize = useDeviceResize('lg');
  const { setError } = formProps;

  const travelDetails = useSelector(travelDataSelector, shallowEqual);
  const calculatorBackendErrors = useSelector(
    (state) => state.travelContract.calculatorBackendErrors,
    shallowEqual,
  );

  const {
    age,
    region,
    days,
    selectedLimit,
    selectedCoverages,
    prices,
    selectedPlan,
    range,
    isFetchingPrice,
    policyPrices,
    selectedDestinations,
  } = travelDetails;

  const dispatchSelectedPlan = useCallback((plan) => dispatch(selectPlan(plan, actionName)), [
    dispatch,
    actionName,
  ]);

  const dispatchSelectCoverages = useCallback(
    (coverages) => dispatch(selectCoverages(coverages, actionName)),
    [dispatch, actionName],
  );

  const dispatchSelectedPackage = useCallback(
    (newPackage) => dispatch(selectPackage(newPackage, actionName)),
    [dispatch, actionName],
  );

  const dispatchSelectedLimit = useCallback(
    (newLimit) => dispatch(selectLimit(newLimit, actionName)),
    [dispatch, actionName],
  );

  const dispatchAge = useCallback((newAge) => dispatch(setAge(newAge, actionName)), [
    dispatch,
    actionName,
  ]);

  const dispatchRegion = useCallback((newRegion) => dispatch(setRegion(newRegion, actionName)), [
    dispatch,
    actionName,
  ]);

  const dispatchDestinations = useCallback(
    (newDestinations) => dispatch(selectDestinations(newDestinations, actionName)),
    [dispatch, actionName],
  );

  const dispatchDays = useCallback((newDays) => dispatch(setDays(newDays, actionName)), [
    dispatch,
    actionName,
  ]);

  const dispatchRange = useCallback((newRange) => dispatch(setRange(newRange, actionName)), [
    dispatch,
    actionName,
  ]);

  const daysWithDebounce = useDebounce(days, TRAVEL_CONTRACT_DAYS_DEBOUNCE);
  const ageWithDebounce = useDebounce(age, TRAVEL_CONTRACT_DAYS_DEBOUNCE);

  const hasOnlyLiteDisallowedCountry = useMemo(
    () => getOnlyLiteDisallowedCountry(selectedDestinations), [selectedDestinations],
  );

  useEffect(() => {
    if (shouldRequestPrice) {
      dispatch(
        getPrices(
          {
            age: ageWithDebounce,
            travelers,
            region,
            days: daysWithDebounce,
            selectedLimit,
            selectedCoverages,
            selectedDestinations,
            selectedPlan,
          },
          actionName,
        ),
      );
    }
  }, [
    ageWithDebounce,
    travelers,
    region,
    daysWithDebounce,
    selectedLimit,
    selectedCoverages,
    actionName,
    shouldRequestPrice,
    dispatch,
    selectedDestinations,
    selectedPlan,
  ]);

  useEffect(() => {
    dispatch(loadAvailableDestinations());
  }, [dispatch]);

  useEffect(() => {
    if (calculatorBackendErrors) {
      const errorList = [];
      each(calculatorBackendErrors, (e, key) => {
        errorList.push({
          type: 'backend',
          name: key,
          message: `backendErrors.${e}`,
        });
      });
      setError(errorList);
    }
  }, [calculatorBackendErrors, setError]);

  const plansWithAllowedLimitRegionAndAge = useMemo(
    () => getPlansWithAllowedLimitRegionAndAge(region, age, hasOnlyLiteDisallowedCountry),
    [age, region, hasOnlyLiteDisallowedCountry],
  );

  const contractDetails = useMemo(
    () => {
      const selectedRegion = getSelectedRegionForSummaryContract(selectedDestinations, region);
      return bindSummaryContractData(selectedLimit, selectedPlan, range, selectedRegion, days, 'travel', t);
    },
    [days, range, region, selectedLimit, selectedPlan, t, selectedDestinations],
  );

  const activeCoverages = useMemo(
    () => getActiveCoverages(prices, t, selectedPlan, isFetchingPrice),
    [prices, t, selectedPlan, isFetchingPrice],
  );

  const discountPrice = useMemo(() => getDiscountPrice(policyPrices, selectedPlan), [
    policyPrices,
    selectedPlan,
  ]);

  const contractSummary = useMemo(
    () => getContractSummary(prices, t, selectedPlan, discountPrice, isFetchingPrice),
    [prices, t, selectedPlan, discountPrice, isFetchingPrice],
  );

  const totalPriceAfterDiscount = useMemo(() => getDiscountedPrice(policyPrices, selectedPlan), [
    policyPrices,
    selectedPlan,
  ]);

  const basePrice = useMemo(() => getBasePrice(prices, selectedPlan), [prices, selectedPlan]);

  return (
    <>
      <EfesContentHeader
        headerText="travelCalculator.headerText"
        headerImage={TravelCalcHeaderImage}
      />
      <Grid container>
        <Grid
          item
          style={{
            width: isDeviceLgSize ? FULL_WIDTH : EFES_CONTENT_WIDTH,
            paddingRight: isDeviceLgSize ? 0 : 16,
          }}
        >
          <TravelCalculator
            age={age}
            setAge={dispatchAge}
            showAgeInput={showAgeInput}
            travelDetails={travelDetails}
            prices={prices}
            setSelectedPlan={dispatchSelectedPlan}
            onCoverageChange={dispatchSelectCoverages}
            setSelectedLimit={dispatchSelectedLimit}
            setRegion={dispatchRegion}
            setDestinations={dispatchDestinations}
            setDays={dispatchDays}
            setRange={dispatchRange}
            setSelectedPackage={dispatchSelectedPackage}
            formProps={formProps}
            hasNarrowView={hasNarrowView}
            amount={basePrice}
            plansWithAllowedLimitRegionAndAge={plansWithAllowedLimitRegionAndAge}
            handleOpenMobileSummary={() => setOpenMobileSummary(!openMobileSummary)}
            buyButtonHandler={buyButtonHandler}
            actionName={actionName}
            totalPriceAfterDiscount={totalPriceAfterDiscount}
          />
        </Grid>
        <Grid
          item
          style={{
            width: isDeviceLgSize ? FULL_WIDTH : EFES_SUMMARY_WIDTH,
            paddingLeft: isDeviceLgSize ? 0 : 16,
          }}
        >
          <div className={summaryWrapperClasses.summaryWrapperBox}>
            <CalculatorSummary
              isFetchingPrice={isFetchingPrice}
              amount={totalPriceAfterDiscount}
              days={travelDetails.days}
              selectedLimit={selectedLimit}
              contractDetails={contractDetails}
              activeCoverages={activeCoverages}
              contractSummary={contractSummary}
              openMobileSummary={openMobileSummary}
              onDismiss={() => setOpenMobileSummary(!openMobileSummary)}
              buyButtonHandler={buyButtonHandler}
            />
          </div>
        </Grid>
      </Grid>
    </>
  );
};

TravelCalculatorContainer.propTypes = {
  travelers: PropTypes.arrayOf(PropTypes.shape({})),
  showAgeInput: PropTypes.bool,
  shouldRequestPrice: PropTypes.bool,
  travelDataSelector: PropTypes.func,
  formProps: PropTypes.shape({
    errors: PropTypes.shape({}),
    setValue: PropTypes.func,
    register: PropTypes.func,
    setError: PropTypes.func,
  }).isRequired,
  actionName: PropTypes.string,
  hasNarrowView: PropTypes.bool,
  buyButtonHandler: PropTypes.func.isRequired,
};

TravelCalculatorContainer.defaultProps = {
  travelers: [],
  showAgeInput: true,
  shouldRequestPrice: true,
  travelDataSelector: (state) => state.travelCalculation,
  actionName: ACTION_NAME.CALCULATOR,
  hasNarrowView: false,
};

export default TravelCalculatorContainer;
